import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { CircleAlert, FileText, Paperclip, RefreshCw, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { ApiError, api, uploadFile, type FilePolicy, type UploadedFile } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatBytes } from '../lib/format';
import { Button, buttonClass } from './ui';

/**
 * A payment's transfer receipt (operator, 2026-10-12: "when record a payment let
 * me upload pdf or image for transfer receipt make it optional").
 *
 * One file — the PDF the bank sent or a picture of the slip — uploaded the
 * moment it is picked or dropped, as the admin's attachments are; the payment
 * form then saves which one it is. The API takes the same types and checks them
 * again (domain/file-policy.ts, RECEIPT_TYPES). The file is kept as it was
 * picked: a receipt is read for its small print, so it is never made smaller.
 */

const RECEIPT_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const RECEIPT_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp'];
const ACCEPT = [...RECEIPT_EXTENSIONS, ...RECEIPT_TYPES].join(',');

const isPicture = (mime: string) => mime.startsWith('image/');

function isReceiptType(file: File): boolean {
  const extension = /\.[^.]+$/.exec(file.name.toLowerCase())?.[0] ?? '';
  return RECEIPT_EXTENSIONS.includes(extension) && (file.type === '' || RECEIPT_TYPES.includes(file.type));
}

interface PendingReceipt {
  name: string;
  size: number;
  mimeType: string;
  progress: number;
  /** A picture shows from the disk while it uploads. */
  previewUrl?: string;
}

export type ReceiptUpload = ReturnType<typeof useReceiptUpload>;

/** The receipt's state for its form: the uploaded file to save, and whether one is still on its way up. */
export function useReceiptUpload() {
  const { t } = useI18n();
  const policy = useQuery({
    queryKey: ['files', 'policy'],
    queryFn: () => api.get<FilePolicy>('/files/policy'),
    staleTime: 5 * 60_000,
  }).data;
  const [file, setFile] = useState<UploadedFile | null>(null);
  const [pending, setPending] = useState<PendingReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const preview = useRef<string | undefined>(undefined);

  const settle = useCallback(() => {
    controller.current = null;
    if (preview.current) URL.revokeObjectURL(preview.current);
    preview.current = undefined;
    setPending(null);
  }, []);

  const describe = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiError) {
        if (e.code === 'FILE_TOO_LARGE') {
          const mb = (e.details as { maxMb?: number } | undefined)?.maxMb ?? policy?.maxUploadMb;
          return mb ? t('payments.transferReceiptTooLarge', { mb }) : e.message;
        }
        if (e.code === 'FILE_TYPE_NOT_ALLOWED') return t('payments.transferReceiptWrongType');
        if (['FILE_STORAGE_UNREACHABLE', 'FILE_STORAGE_REFUSED', 'FILE_STORAGE_UNAVAILABLE'].includes(e.code)) {
          return t('payments.transferReceiptStorage');
        }
      }
      return t('payments.transferReceiptFailed', { reason: e instanceof Error ? e.message : String(e) });
    },
    [policy, t],
  );

  /** A new pick replaces what was there once it is up; a bad one leaves it as it was. */
  const pick = useCallback(
    (picked: File) => {
      controller.current?.abort();
      settle();
      if (!isReceiptType(picked)) return setError(t('payments.transferReceiptWrongType'));
      if (policy && picked.size > policy.maxUploadMb * 1024 * 1024) {
        return setError(t('payments.transferReceiptTooLarge', { mb: policy.maxUploadMb }));
      }

      setError(null);
      const abort = new AbortController();
      controller.current = abort;
      preview.current = isPicture(picked.type) ? URL.createObjectURL(picked) : undefined;
      setPending({ name: picked.name, size: picked.size, mimeType: picked.type, progress: 0, previewUrl: preview.current });
      uploadFile(picked, picked.name, {
        signal: abort.signal,
        onProgress: (progress) => setPending((p) => (p && controller.current === abort ? { ...p, progress } : p)),
      })
        .then((uploaded) => {
          if (controller.current !== abort) return;
          setFile(uploaded);
          settle();
        })
        .catch((e) => {
          if (abort.signal.aborted) return;
          setError(describe(e));
          settle();
        });
    },
    [describe, policy, settle, t],
  );

  /** Stops an upload on its way, or takes the receipt off. */
  const remove = useCallback(() => {
    if (controller.current) {
      controller.current.abort();
      settle();
    } else {
      setFile(null);
    }
    setError(null);
  }, [settle]);

  // Leaving the form stops an upload still running, and lets its picture go.
  useEffect(
    () => () => {
      controller.current?.abort();
      if (preview.current) URL.revokeObjectURL(preview.current);
    },
    [],
  );

  return { file, pending, error, policy, busy: pending !== null, pick, remove };
}

/**
 * The receipt on the payment form: pick or drop one file, see it upload, open
 * it, replace it or take it off. Not a `Field` — that is a `<label>`, and a label
 * around buttons clicks the first of them whenever its text is clicked.
 */
export function ReceiptPicker({ receipt, className }: { receipt: ReceiptUpload; className?: string }) {
  const { t, locale } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const { file, pending, error } = receipt;
  const choose = () => input.current?.click();

  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
  const dropZone = {
    onDragEnter: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) receipt.pick(dropped);
    },
  };

  return (
    <div className={className}>
      <p className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">{t('payments.transferReceipt')}</p>
      <div
        {...dropZone}
        className={clsx(
          'relative rounded-md border border-dashed p-3 transition-colors duration-fast',
          dragging ? 'border-primary bg-primary/5' : 'hairline',
        )}
      >
        {pending ? (
          <div className="flex items-center gap-3">
            <Thumbnail mimeType={pending.mimeType} src={pending.previewUrl} name={pending.name} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-body-sm text-[var(--text-primary)]">
                <bdi>{pending.name}</bdi>
              </p>
              <p className="text-caption text-muted">
                {formatBytes(pending.size, locale)} · {t('payments.transferReceiptUploading')}{' '}
                <span className="tabular" dir="ltr">{Math.round(pending.progress * 100)}%</span>
              </p>
              <span className="mt-1 block h-1 overflow-hidden rounded-full bg-[var(--line)]">
                <span
                  className="block h-full rounded-full bg-primary transition-[width] duration-fast"
                  style={{ width: `${Math.round(pending.progress * 100)}%` }}
                />
              </span>
            </div>
            <Button variant="ghost" size="sm" icon={<X className="h-4 w-4" aria-hidden />} onClick={receipt.remove}>
              {t('common.cancel')}
            </Button>
          </div>
        ) : file ? (
          <div className="flex flex-wrap items-center gap-3">
            <a href={file.url} target="_blank" rel="noopener noreferrer" title={t('payments.transferReceiptOpen')} className="shrink-0">
              <Thumbnail mimeType={file.mimeType} src={file.url} name={file.originalName} />
            </a>
            <div className="min-w-0 flex-1">
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                title={t('payments.transferReceiptOpen')}
                className="block truncate text-body-sm font-medium text-[var(--text-primary)] hover:text-primary-600 dark:hover:text-primary-400 hover:underline"
              >
                <bdi>{file.originalName}</bdi>
              </a>
              <p className="text-caption text-muted">{formatBytes(file.sizeBytes, locale)}</p>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" icon={<RefreshCw className="h-4 w-4" aria-hidden />} onClick={choose}>
                {t('payments.transferReceiptReplace')}
              </Button>
              <Button variant="ghost" size="sm" icon={<Trash2 className="h-4 w-4" aria-hidden />} onClick={receipt.remove}>
                {t('payments.transferReceiptRemove')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Button size="sm" icon={<Paperclip className="h-4 w-4" aria-hidden />} onClick={choose}>
              {t('payments.transferReceiptAdd')}
            </Button>
            <span className="text-caption text-muted">{t('payments.transferReceiptDrop')}</span>
          </div>
        )}
        {/* Opaque: Tailwind gives `bg-[var(--x)]/NN` no colour at all. */}
        {dragging && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center rounded-md border-2 border-dashed border-primary bg-[var(--surface-raised)] text-body-sm font-medium text-primary-600 dark:text-primary-400">
            {t('payments.transferReceiptDropHere')}
          </div>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = '';
          if (picked) receipt.pick(picked);
        }}
      />
      {error ? (
        <p className="mt-1.5 flex items-start gap-1.5 text-caption text-error-fg dark:text-error-dark" role="alert">
          <CircleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : (
        <p className="mt-1.5 text-caption text-faint">
          {receipt.policy ? t('payments.transferReceiptHint', { mb: receipt.policy.maxUploadMb }) : t('payments.transferReceiptHintPlain')}
        </p>
      )}
    </div>
  );
}

/** A picture as itself; a PDF as its icon. */
function Thumbnail({ mimeType, src, name }: { mimeType: string; src?: string; name: string }) {
  return (
    <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-md border hairline bg-[var(--surface-sunken)]">
      {isPicture(mimeType) && src ? (
        <img src={src} alt={name} className="h-full w-full object-cover" />
      ) : (
        <FileText className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden />
      )}
    </span>
  );
}

/**
 * A payment's transfer receipt from a payments table: opens in a tab of its
 * own, where the browser shows the PDF or the picture. It never lets the click
 * through: payment rows navigate when clicked.
 */
export function ReceiptLink({ file, size = 'md' }: { file: UploadedFile; size?: 'sm' | 'md' }) {
  const { t } = useI18n();
  return (
    <a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${t('payments.transferReceiptOpen')} — ${file.originalName}`}
      aria-label={t('payments.transferReceiptOpen')}
      onClick={(e) => e.stopPropagation()}
      className={buttonClass('secondary', size)}
    >
      <Paperclip className="h-4 w-4" aria-hidden />
    </a>
  );
}
